/**
 * Componente CookieBanner
 * Banner de consentimiento de cookies GDPR con configuración avanzada
 */

import React from 'react';
import { useCookieConsent } from '../../context/CookieContext';
import Button from '../common/Button';
import { Link } from 'react-router-dom';

/**
 * Banner de cookies GDPR
 * Muestra el aviso de cookies y permite configurar preferencias
 * @returns {JSX.Element|null} Banner de cookies o null si está oculto
 */
const CookieBanner = () => {
  const { showBanner, showConfig, acceptAll, rejectAll, openConfig, closeConfig, savePreferences, consent } = useCookieConsent();

  // Estado local para las preferencias en el modal de configuración
  const [tempConsent, setTempConsent] = React.useState(consent);

  // Actualizar tempConsent cuando cambie consent
  React.useEffect(() => {
    setTempConsent(consent);
  }, [consent]);

  // No mostrar nada si el banner está oculto
  if (!showBanner) {
    return null;
  }

  /**
   * Maneja el cambio de una preferencia de cookie
   * @param {string} type - Tipo de cookie
   */
  const handleToggle = (type) => {
    setTempConsent((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  /**
   * Guarda las preferencias personalizadas
   */
  const handleSavePreferences = () => {
    savePreferences(tempConsent);
  };

  return (
    <>
      {/* Overlay de fondo */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" />

      {/* Banner principal */}
      {!showConfig ? (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-2xl z-50 border-t-4 border-intercasa-red">
          <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Icono y texto */}
              <div className="flex items-start space-x-4 flex-1">
                {/* Icono de cookies SVG */}
                <div className="flex-shrink-0">
                  <svg
                    className="w-12 h-12 text-intercasa-red"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M21.598 11.064a1.006 1.006 0 0 0-.854-.172A2.938 2.938 0 0 1 20 11c-1.654 0-3-1.346-3-3 0-.217.031-.444.099-.716a1 1 0 0 0-1.067-1.236A9.956 9.956 0 0 0 2 12c0 5.514 4.486 10 10 10s10-4.486 10-10c0-.049-.003-.097-.007-.16a1.004 1.004 0 0 0-.395-.776zM12 20c-4.411 0-8-3.589-8-8a7.962 7.962 0 0 1 6.006-7.75A5.006 5.006 0 0 0 15 9l.101-.001a5.007 5.007 0 0 0 4.837 4.879A7.977 7.977 0 0 1 12 20z" />
                    <circle cx="12.5" cy="11.5" r="1.5" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <circle cx="7.5" cy="12.5" r="1.5" />
                    <circle cx="15.5" cy="15.5" r="1.5" />
                    <circle cx="10.5" cy="16.5" r="1.5" />
                  </svg>
                </div>

                {/* Texto */}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-intercasa-dark mb-2">
                    Respetamos tu privacidad
                  </h3>
                  <p className="text-sm text-intercasa-gray leading-relaxed">
                    Utilizamos cookies propias y de terceros para mejorar nuestros servicios y mostrarle publicidad relacionada con sus preferencias mediante el análisis de sus hábitos de navegación. Si continúa navegando, consideramos que acepta su uso. Puede obtener más información, o bien conocer cómo cambiar la configuración, en nuestra{' '}
                    <Link to="/cookies" className="text-intercasa-red underline hover:text-red-700">
                      Política de Cookies
                    </Link>
                    {' '}y{' '}
                    <Link to="/privacidad" className="text-intercasa-red underline hover:text-red-700">
                      Política de Privacidad
                    </Link>
                    .
                  </p>
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <Button variant="secondary" onClick={openConfig} className="whitespace-nowrap">
                  Configurar
                </Button>
                <Button variant="primary" onClick={acceptAll} className="whitespace-nowrap">
                  Permitir Todo
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Panel de configuración detallada */
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header del modal */}
            <div className="bg-intercasa-red text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold">Configuración de Cookies</h2>
              <button
                onClick={closeConfig}
                className="text-white hover:text-gray-200 transition-colors"
                aria-label="Cerrar"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenido del modal */}
            <div className="px-6 py-4 space-y-6">
              {/* Cookies necesarias */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-lg">Cookies Necesarias</h3>
                  <span className="text-sm text-intercasa-gray font-semibold">Siempre activas</span>
                </div>
                <p className="text-sm text-intercasa-gray">
                  Estas cookies son necesarias para el funcionamiento básico del sitio web y no se pueden desactivar.
                </p>
              </div>

              {/* Cookies funcionales */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-lg">Cookies Funcionales</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tempConsent.functional}
                      onChange={() => handleToggle('functional')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-intercasa-red/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-intercasa-red"></div>
                  </label>
                </div>
                <p className="text-sm text-intercasa-gray">
                  Permiten mejorar la funcionalidad del sitio web y personalizar tu experiencia.
                </p>
              </div>

              {/* Cookies analíticas */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-lg">Cookies Analíticas</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tempConsent.analytics}
                      onChange={() => handleToggle('analytics')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-intercasa-red/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-intercasa-red"></div>
                  </label>
                </div>
                <p className="text-sm text-intercasa-gray">
                  Nos ayudan a entender cómo los visitantes interactúan con el sitio web mediante datos estadísticos anónimos.
                </p>
              </div>

              {/* Cookies publicitarias */}
              <div className="pb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-lg">Cookies Publicitarias</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tempConsent.advertising}
                      onChange={() => handleToggle('advertising')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-intercasa-red/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-intercasa-red"></div>
                  </label>
                </div>
                <p className="text-sm text-intercasa-gray">
                  Se utilizan para mostrar anuncios relevantes basados en tus intereses y comportamiento de navegación.
                </p>
              </div>
            </div>

            {/* Footer del modal con botones */}
            <div className="px-6 py-4 bg-gray-50 flex flex-col sm:flex-row gap-3 justify-end">
              <Button variant="secondary" onClick={rejectAll}>
                Rechazar Todo
              </Button>
              <Button variant="primary" onClick={handleSavePreferences}>
                Guardar Preferencias
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
